import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Boxes, PackageOpen, Camera, Bell, Users, AlertTriangle, X } from "lucide-react";
import { Button } from "./ui/button";

// Each slide highlights ONE real UI element the user will tap. Icons match
// the actual buttons on the Dashboard so the tour feels like a tooltip
// over the live app, not a marketing sequence.
const SLIDES = [
  {
    id: "step-cards",
    step: "1 of 4",
    title: "Your home screen",
    body: "Three cards at the top tell you everything at a glance:",
    bullets: [
      {
        icon: Clock,
        bg: "#FBEAE5",
        fg: "#B04A2A",
        label: "Likely to Spoil Soon",
        text: "Food expiring in the next 2 days — your most urgent stuff.",
      },
      {
        icon: AlertTriangle,
        bg: "#FBE2DA",
        fg: "#8C2A0F",
        label: "Risks chip",
        text: "A red badge appears here when food has already passed best-by. Tap it to review and decide.",
      },
      {
        icon: Boxes,
        bg: "#DCEDF0",
        fg: "#1F5566",
        label: "Total Active",
        text: "Every container you're currently storing.",
      },
      {
        icon: PackageOpen,
        bg: "#E8F0E5",
        fg: "#4A8A78",
        label: "Unused",
        text: "Empty containers waiting for new food.",
      },
    ],
  },
  {
    id: "step-scan",
    step: "2 of 4",
    title: "The scan button",
    body: "The big camera button at the bottom is how you log food. Tap it, scan a QFresh sticker, and fill in what's inside — name, calories, when it expires.",
    bullets: [
      {
        icon: Camera,
        bg: "#FFF6E6",
        fg: "#B27300",
        label: "Scan / Add",
        text: "Lives in the bottom nav. Use it every time you put food in or take food out.",
      },
    ],
  },
  {
    id: "step-notif",
    step: "3 of 4",
    title: "Stay in the loop",
    body: "The bell icon in the top-right shows alerts about food about to spoil. We'll also email you if you'd like — turn that on in Profile → Notifications.",
    bullets: [
      {
        icon: Bell,
        bg: "#DCEDF0",
        fg: "#1F5566",
        label: "Bell icon",
        text: "A red number tells you how many unread alerts you have. Tap it to see what's expiring and act fast.",
      },
    ],
  },
  {
    id: "step-family",
    step: "4 of 4",
    title: "Make it a family hub",
    body: "Invite anyone in your home so everyone sees the same fridge. Then log your first container and watch QFresh start saving you money on groceries.",
    bullets: [
      {
        icon: Users,
        bg: "#E8D5F2",
        fg: "#5A3A8A",
        label: "Invite household members",
        text: "Profile → Household → share your join code.",
      },
      {
        icon: Camera,
        bg: "#FFF6E6",
        fg: "#B27300",
        label: "Log your first container",
        text: "Hit the scan button now to add something you already have in the fridge.",
      },
    ],
  },
];

export const HowItWorks = ({ open, onClose }) => {
  const [i, setI] = useState(0);
  const isLast = i === SLIDES.length - 1;
  const slide = SLIDES[i];

  const finish = () => {
    setI(0);
    onClose?.();
  };

  const next = () => (isLast ? finish() : setI((n) => n + 1));
  const prev = () => setI((n) => Math.max(0, n - 1));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/55 backdrop-blur-md flex items-end sm:items-center justify-center"
          data-testid="how-it-works-overlay"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            className="w-full max-w-[480px] bg-[#FDFBF7] rounded-t-3xl sm:rounded-3xl sm:m-4 overflow-hidden max-h-[90vh] flex flex-col"
          >
            <div className="flex items-center justify-between px-5 pt-5 shrink-0">
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#8A93A6]">
                Quick tour · {slide.step}
              </span>
              <button
                onClick={finish}
                data-testid="tour-skip-btn"
                className="text-xs text-[#4F5D75] hover:text-[#1F5566] flex items-center gap-1"
              >
                Skip <X className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="px-7 pb-7 pt-3 flex-1 overflow-y-auto">
              <AnimatePresence mode="wait">
                <motion.div
                  key={i}
                  initial={{ x: 40, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -40, opacity: 0 }}
                  transition={{ duration: 0.32, ease: "easeOut" }}
                  className="flex flex-col"
                  data-testid={`tour-slide-${i}`}
                >
                  <h2 className="font-heading text-2xl font-bold brand-gradient-text leading-tight">
                    {slide.title}
                  </h2>
                  <p className="text-sm text-[#4F5D75] mt-2 leading-relaxed">
                    {slide.body}
                  </p>

                  {/* Per-feature callouts — each one mirrors a real Dashboard
                      element. Icons + colors match the live UI so the user
                      can map this directly to what they'll see when the
                      tour closes. */}
                  <ul className="mt-4 space-y-2.5">
                    {slide.bullets.map((b) => {
                      const Icon = b.icon;
                      return (
                        <li
                          key={b.label}
                          className="flex items-start gap-3 bg-white border border-[#E2E0D8] rounded-2xl p-3"
                          data-testid={`tour-bullet-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                          <div
                            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                            style={{ backgroundColor: b.bg }}
                          >
                            <Icon
                              className="w-4 h-4"
                              style={{ color: b.fg }}
                              strokeWidth={2.4}
                            />
                          </div>
                          <div className="min-w-0">
                            <div className="font-heading text-sm font-bold text-[#2D3142]">
                              {b.label}
                            </div>
                            <div className="text-xs text-[#4F5D75] mt-0.5 leading-snug">
                              {b.text}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="px-7 pb-6 pt-2 shrink-0">
              <div className="flex items-center justify-center gap-2 mb-4">
                {SLIDES.map((s, idx) => (
                  <button
                    key={s.id}
                    onClick={() => setI(idx)}
                    data-testid={`tour-dot-${idx}`}
                    aria-label={`Go to slide ${idx + 1}`}
                    className={`h-2 rounded-full transition-all ${
                      idx === i ? "w-7 brand-gradient-bg" : "w-2 bg-[#E2E0D8]"
                    }`}
                  />
                ))}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={prev}
                  disabled={i === 0}
                  data-testid="tour-prev-btn"
                  className="flex-1 border-[#E2E0D8] disabled:opacity-40"
                >
                  Back
                </Button>
                <Button
                  onClick={next}
                  data-testid="tour-next-btn"
                  className="flex-[1.5] brand-gradient-bg text-white"
                >
                  {isLast ? "Got it — let's go" : "Next"}
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
